// Onset detection via RMS envelope + adaptive threshold.
//
// Not spectral flux: that needs FFT which is awkward without a dep. For
// drum-loop-style content (sharp transients against a quiet background)
// an RMS rising-edge detector is almost as good and a fraction of the
// code. For tonal / legato material it underperforms, but that isn't
// the chop verb's target.
//
// Algorithm per hop:
//   1. Compute RMS over a window of samples.
//   2. Smooth a bit with a 3-hop average.
//   3. Compute a rolling baseline (mean of the N preceding hops).
//   4. Mark an onset when the current RMS > baseline * ratio AND it's a
//      local maximum AND a minimum gap has elapsed since the previous
//      onset.
//
// Output: frame indices into the original buffer.

export type OnsetOptions = {
  /** Analysis window size in samples. Default 1024. */
  windowSize?: number;
  /** Hop between windows in samples. Default 512. */
  hopSize?: number;
  /** Required ratio current-RMS / baseline to count as an onset. 1.5 is
   *  sensitive; 3 is conservative. Default 1.8. */
  thresholdRatio?: number;
  /** Minimum gap between onsets in seconds. Prevents a single transient
   *  from spawning multiple closely-spaced onsets. Default 0.05s. */
  minGapSeconds?: number;
  /** Window length for the rolling baseline, in hops. Default 24. */
  baselineHops?: number;
};

export function detectOnsets(
  channel: Float32Array,
  sampleRate: number,
  opts: OnsetOptions = {},
): number[] {
  const windowSize = opts.windowSize ?? 1024;
  const hopSize = opts.hopSize ?? 512;
  const thresholdRatio = opts.thresholdRatio ?? 1.8;
  const minGapSeconds = opts.minGapSeconds ?? 0.05;
  const baselineHops = opts.baselineHops ?? 24;

  if (channel.length < windowSize) return [];

  // Step 1+2: RMS per hop, then a 3-point smooth.
  const hopCount = Math.floor((channel.length - windowSize) / hopSize) + 1;
  const rms = new Float32Array(hopCount);
  for (let h = 0; h < hopCount; h++) {
    const start = h * hopSize;
    let sum = 0;
    for (let i = 0; i < windowSize; i++) {
      const v = channel[start + i] ?? 0;
      sum += v * v;
    }
    rms[h] = Math.sqrt(sum / windowSize);
  }
  const smoothed = new Float32Array(hopCount);
  for (let h = 0; h < hopCount; h++) {
    const a = rms[Math.max(0, h - 1)]!;
    const b = rms[h]!;
    const c = rms[Math.min(hopCount - 1, h + 1)]!;
    smoothed[h] = (a + b + c) / 3;
  }

  // Step 3+4: rolling baseline, threshold, local-max, min-gap.
  const minGapHops = Math.max(1, Math.round((minGapSeconds * sampleRate) / hopSize));
  const onsets: number[] = [];
  let lastOnsetHop = -Infinity;

  for (let h = 0; h < hopCount; h++) {
    const base = rollingMean(smoothed, h, baselineHops);
    const current = smoothed[h]!;
    const prev = smoothed[Math.max(0, h - 1)]!;
    const next = smoothed[Math.min(hopCount - 1, h + 1)]!;

    const isAboveThreshold = current > base * thresholdRatio && base > 0.001;
    const isRisingEdge = current >= prev && current > next;
    const hasGap = h - lastOnsetHop >= minGapHops;

    if (isAboveThreshold && isRisingEdge && hasGap) {
      onsets.push(h * hopSize);
      lastOnsetHop = h;
    }
  }
  return onsets;
}

function rollingMean(
  data: Float32Array,
  endHop: number,
  window: number,
): number {
  const start = Math.max(0, endHop - window);
  const len = endHop - start;
  if (len === 0) return data[endHop] ?? 0;
  let sum = 0;
  for (let i = start; i < endHop; i++) sum += data[i]!;
  return sum / len;
}

// Given onset frames + the buffer length, return slice pairs so each
// slice covers [start, end). Always guarantees the first slice starts at
// 0 unless the first onset is at 0 (avoids a silent leading chunk). The
// final slice runs to end of buffer.
export function onsetsToSlices(
  onsets: number[],
  totalFrames: number,
): Array<{ start: number; end: number }> {
  if (onsets.length === 0) return [];
  const starts = onsets[0] === 0 ? [...onsets] : [0, ...onsets];
  const slices: Array<{ start: number; end: number }> = [];
  for (let i = 0; i < starts.length; i++) {
    const start = starts[i]!;
    const end = i + 1 < starts.length ? starts[i + 1]! : totalFrames;
    if (end > start) slices.push({ start, end });
  }
  return slices;
}
